import type {
    CollarsHistoryItemDto, CollarsTerminalHistoryDto, CollarsSessionDto, CollarsHistoryPositionEvent,
    CollarsHistoryLedgerEvent, CollarsLedgerDto
} from '../../shell-communication/collars-operations';

export type HistoryUpdate = 'ignored' | 'changed' | 'reload';

/** Dialog-owned book. No timers, transport or grid dependencies. Events replace full position state. */
export class CollarsSessionHistoryStateService {
    private books: {[terminal: string]: CollarsTerminalHistoryDto} = {};
    private revisions: {[terminal: string]: {[position: string]: number}} = {};
    private retired: {[terminal: string]: string[]} = {};
    private scope: string | null = null;

    reset(scope: string | null = null): void { this.books = {}; this.revisions = {}; this.retired = {}; this.scope = scope; }
    terminalIds(): string[] { return Object.keys(this.books); }
    book(id: string): CollarsTerminalHistoryDto { return this.books[id]; }
    sessions(): CollarsSessionDto[] {
        return this.terminalIds().reduce((all, id) => all.concat(this.books[id].sessions), [] as CollarsSessionDto[]);
    }
    remove(id: string): void { delete this.books[id]; delete this.revisions[id]; delete this.retired[id]; }
    replace(dto: CollarsTerminalHistoryDto): boolean {
        const old = this.books[dto.terminalId];
        const retired = this.retired[dto.terminalId] || (this.retired[dto.terminalId] = []);
        if (retired.indexOf(dto.streamGeneration) >= 0) { return false; }
        if (old && old.streamGeneration === dto.streamGeneration) {
            const seen = this.revisions[dto.terminalId];
            const latest = Object.keys(seen).reduce((revision, id) => Math.max(revision, seen[id]), old.capturedRevision);
            if (dto.capturedRevision < latest) { return false; }
        } else if (old) { retired.push(old.streamGeneration); }
        this.books[dto.terminalId] = dto;
        this.revisions[dto.terminalId] = {};
        this.recalculate(dto);
        return true;
    }

    position(p: CollarsHistoryPositionEvent): HistoryUpdate {
        const book = this.books[p.terminalId];
        if (!book) { return 'ignored'; }
        if ((this.retired[p.terminalId] || []).indexOf(p.streamGeneration) >= 0) { return 'ignored'; }
        if (!this.sameStream(book, p)) { return 'reload'; }
        const previous = this.revisions[p.terminalId][p.positionId] || book.capturedRevision;
        if (p.eventRevision <= previous) { return 'ignored'; }
        const active = book.sessions.find(s => s.isActive);
        if (!active || p.sessionId !== active.sessionId) { return 'reload'; }
        this.revisions[p.terminalId][p.positionId] = p.eventRevision;
        const old = book.activeItems.find(i => i.positionId === p.positionId);
        book.activeItems = book.activeItems.filter(i => i.positionId !== p.positionId);
        const belongs = !this.scope || [p.portfolioId, p.comboId, p.groupId].indexOf(this.scope) >= 0;
        const relevant = p.hadActivityThisSession || p.netPosition !== 0 || Math.abs(p.sessionTotalPnL) > 1e-9;
        if (!p.isRemoved && belongs && relevant) {
            // Names are metadata, not quote data. Existing names survive a full state replacement.
            const sibling = book.activeItems.find(i => i.groupId === p.groupId);
            const named = old || sibling;
            book.activeItems.push({
                sessionId: p.sessionId, positionId: p.positionId, terminalId: p.terminalId,
                tradingInstrumentCode: p.tradingInstrumentCode, kind: p.kind,
                portfolioId: p.portfolioId, portfolioName: p.portfolioName || (named && named.portfolioName),
                comboId: p.comboId, comboName: p.comboName || (named && named.comboName),
                groupId: p.groupId, groupName: p.groupName || (named && named.groupName),
                netPosition: p.netPosition, avgPx: p.avgPx, lastMatchPx: p.lastMatchPx,
                sessionPnL: p.sessionTotalPnL, accumulatedPnL: p.accumulatedTotalPnL, isInterest: false
            });
        }
        this.recalculate(book);
        return 'changed';
    }

    ledger(r: CollarsHistoryLedgerEvent): HistoryUpdate {
        const book = this.books[r.terminalId];
        if (!book) { return 'ignored'; }
        if ((this.retired[r.terminalId] || []).indexOf(r.streamGeneration) >= 0) { return 'ignored'; }
        if (!this.sameStream(book, r)) { return 'reload'; }
        if (r.eventRevision <= book.capturedRevision) { return 'ignored'; }
        if (r.transactionType === 5) { return 'reload'; } // synthetic Interest has no position event
        if (book.selectedSessionId === r.sessionId && book.ledger && !book.ledger.some(x => x.recordId === r.recordId)) {
            book.ledger = book.ledger.concat(r).sort((a, b) => a.sequence - b.sequence);
        }
        return 'changed';
    }

    items(session: CollarsSessionDto): CollarsHistoryItemDto[] {
        const book = this.books[session.terminalId];
        if (!book) { return []; }
        return session.isActive ? book.activeItems : (book.selectedSessionId === session.sessionId ? book.selectedItems || [] : []);
    }

    selectedLedger(session: CollarsSessionDto, bucket?: string, position?: string): CollarsLedgerDto[] {
        const book = this.books[session.terminalId];
        if (!book || book.selectedSessionId !== session.sessionId) { return []; }
        return (book.ledger || []).filter(r => (!this.scope || [r.portfolioId, r.comboId, r.groupId].indexOf(this.scope) >= 0)
            && (!bucket || [r.portfolioId, r.comboId, r.groupId].indexOf(bucket) >= 0)
            && (!position || r.positionId === position));
    }

    private sameStream(book: CollarsTerminalHistoryDto, event: {streamGeneration: string; eventRevision: number}): boolean {
        return !!event.streamGeneration && book.streamGeneration === event.streamGeneration && event.eventRevision > 0;
    }
    private recalculate(book: CollarsTerminalHistoryDto): void {
        book.sessions = book.sessions.map(s => !s.isActive ? s : Object.assign({}, s, {
            sessionPnL: book.activeItems.reduce((sum, i) => sum + i.sessionPnL, 0),
            accumulatedPnL: book.activeItems.reduce((sum, i) => sum + i.accumulatedPnL, 0)
        }));
    }
}
